import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { groupSelection, ungroup as ungroupTree, descendantIds, childrenOf, remapTreeIds, moveNode } from '../utils/layerTree';
import { createIdbStorage } from '../utils/idbStorage';
import { calculerAlignement } from '../utils/alignement';
import { poserDecoupe, genererDecoupe, regenererDecoupesAuto, reglagesDecoupe, estDecoupe, decoupeAuPremierPlan, ENCRES_DECOUPE } from '../utils/decoupe';

/** Un identifiant de calque neuf, au format de ceux du store. */
const nouvelIdCalque = () => 'el_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);

// Adaptateur IndexedDB (cf. utils/idbStorage.js — il sert aussi au Studio I.A).
// Écriture immédiate, sans regroupement : c'est le comportement de toujours de
// ce magasin, et le rythme de ses écritures est déjà borné par les gestes de
// l'éditeur.
const idbStorage = createIdbStorage();

// Document de départ vide pour les nouveaux comptes — pas de calques.
// Les utilisateurs existants conservent leur état persisté en IndexedDB.
const initialElements = [];

export const useLabelStore = create(persist((set, get) => ({
    // --- Propriétés Globales de l'Étiquette ---
    // Document de départ pour les nouveaux comptes : 130×130 mm (carré),
    // fond blanc, règles affichées. Les utilisateurs existants gardent
    // leur canvas persisté en IndexedDB.
    canvas: {
        width_mm: 130,
        height_mm: 130,
        color_bg: '#FFFFFF',
        scale: 3, // Zoom visuel du canvas dans l'éditeur (1 mm = 3 px par défaut)
        margin_mm: 3, // Repère de sécurité
        bleed_mm: 1.5, // Fond perdu PAR CÔTÉ (3 mm au total) : celui de toute étiquette à imprimer
        showRulers: true,
        showGuides: false,
        showGrid: false,
        panX: 0,
        panY: 0
    },

    // forge-light n'existe plus depuis qu'on a 4 themes clairs distincts
    // (forge-dark-light, forge-slate-light, forge-monochrome-light, forge-creative-light).
    // Les anciens utilisateurs avec uiTheme: 'forge-light' sont migres vers
    // 'forge-dark-light' au load (cf. onRehydrateStorage / init).
    // Défaut : Classique (demande utilisateur 2026-07-16). Le choix effectif
    // persiste en IndexedDB + miroir localStorage (cf. index.html/EditorView).
    uiTheme: 'forge-classic-forge',

    // Voile posé sous un texte pendant l'édition inline (cf. canvas/TextLayer).
    // 'auto' choisit le voile à l'opposé de la couleur du texte ; 'clair',
    // 'sombre' et 'aucun' le forcent. Réglé depuis la barre flottante
    // d'édition du texte (cf. InlineRichTextEditor), seul endroit où le
    // besoin se voit — donc pas dans les Préférences.
    textEditVeil: 'auto',
    setTextEditVeil: (mode) => set({ textEditVeil: mode }),

    // Ajustement automatique du cadre au contenu à l'INSERTION d'un calque
    // (import réglementaire, rafraîchissement de fiche, application d'un modèle
    // .lfgr). C'est le geste Cmd/Ctrl+Alt+F, joué tout seul : demandé par
    // l'utilisateur le 2026-08-21 — un cadre importé n'a aucune raison d'être
    // plus large que son texte. Réglable dans Préférences → Affichage, car
    // l'ajustement resserre des cadres qu'on a parfois dimensionnés à la main.
    autoFitOnImport: true,
    setAutoFitOnImport: (v) => set({ autoFitOnImport: !!v }),

    // --- L'Image IA Centrale (Résultat de Flux) ---
    aiImage: {
        url: null, // Si null, on affiche un placeholder
        x: 100,
        y: 50,
        width: 200,
        height: 150,
    },

    // --- Modele IA ---
    aiModel: "flux1-dev-Q4_1.gguf",
    setAiModel: (model) => set({ aiModel: model }),

    // --- Les Éléments Textes / Visuels de la composition ---
    elements: initialElements,

    // --- Les Éléments actuellement sélectionnés dans l'éditeur ---
    selectedElementIds: [],

    // --- Outil actif ---
    // 'select'  = comportement historique de l'éditeur (déplacer, redimensionner).
    // 'eraser'  = peinture d'un masque alpha sur un calque image.
    // 'marquee' = tracé d'une zone de sélection sur un calque image.
    // 'hand'    = déplacement de la vue. Ce que fait déjà la barre d'espace, mais
    //             en mode permanent — et la barre d'espace s'y INVERSE pour rendre
    //             la flèche le temps d'un geste (cf. LabelCanvas).
    // Délibérément HORS partialize : on ne veut jamais rouvrir l'appli en mode
    // gomme sans l'avoir demandé — la sélection est le seul état de repos sûr.
    activeTool: 'select',
    // Quitter l'outil de zone abandonne la zone tracée : une sélection qu'on ne
    // voit plus mais qui reste armée est un piège (le Suppr suivant effacerait
    // une zone invisible).
    setActiveTool: (tool) => set((state) => (
        tool !== 'marquee' && state.activeTool === 'marquee'
            ? { activeTool: tool, marquee: null, marqueeDraft: null }
            : { activeTool: tool }
    )),

    // Réglages de la gomme. `radius` est en MILLIMÈTRES (unité du document) :
    // le trait garde ainsi la même épaisseur réelle quel que soit le zoom.
    // `hardness` : 'hard' = bord net, 'soft' = dégradé sur la moitié externe.
    eraser: { radius: 5, hardness: 'hard' },
    setEraser: (partial) => set((state) => ({ eraser: { ...state.eraser, ...partial } })),

    // Réglages de l'outil de sélection de zone. `shape` est la forme armée,
    // `feather` l'adoucissement du bord en MILLIMÈTRES (0 = bord net).
    selection: { shape: 'rect', feather: 0 },
    setSelection: (partial) => set((state) => ({ selection: { ...state.selection, ...partial } })),

    // La zone TRACÉE, distincte des réglages ci-dessus.
    // `{ elementId, shape, points: [[u,v], …] }` en coordonnées normalisées du
    // masque du calque (cf. utils/selection.js). Transitoire : ni persistée, ni
    // versionnée dans l'historique — c'est un état d'interface, comme un curseur
    // de texte. Seul son EFFET (le masque de gomme) entre dans l'historique.
    marquee: null,
    setMarquee: (marquee) => set({ marquee, marqueeDraft: null }),
    clearMarquee: () => set({ marquee: null, marqueeDraft: null }),

    // Lasso polygonal en cours de construction. Il vit dans le store et non dans
    // une ref parce que ses sommets se posent au CLIC — quelques écritures par
    // tracé, là où un rectangle en émettrait cent par seconde. Cela permet au
    // gestionnaire de clavier unique de LabelCanvas de savoir qu'un tracé est en
    // cours, et donc d'affecter Échap et Entrée au polygone plutôt qu'à l'outil.
    marqueeDraft: null,
    setMarqueeDraft: (marqueeDraft) => set({ marqueeDraft }),

    // Palette d'outils flottante. `x`/`y` à null = pas encore positionnée,
    // la palette se place alors d'elle-même contre le bord gauche du canvas.
    toolPalette: { open: true, x: null, y: null },
    setToolPalette: (partial) => set((state) => ({ toolPalette: { ...state.toolPalette, ...partial } })),

    // --- Historique (Undo/Redo) ---
    history: [],
    historyIndex: -1,

    _pushState: (actionName) => set((state) => {
        const snapshot = {
            elements: JSON.parse(JSON.stringify(state.elements)),
            aiImage: JSON.parse(JSON.stringify(state.aiImage)),
            canvas: JSON.parse(JSON.stringify(state.canvas)),
            actionName: actionName || 'Modification'
        };
        const newHistory = state.history.slice(0, state.historyIndex + 1);
        newHistory.push(snapshot);
        if (newHistory.length > 50) newHistory.shift();
        return { history: newHistory, historyIndex: newHistory.length - 1 };
    }),

    undo: () => set((state) => {
        if (state.historyIndex > 0) {
            const newIndex = state.historyIndex - 1;
            const pastState = state.history[newIndex];
            return {
                elements: pastState.elements,
                aiImage: pastState.aiImage,
                canvas: pastState.canvas,
                historyIndex: newIndex,
                selectedElementIds: []
            };
        }
        return state;
    }),

    redo: () => set((state) => {
        if (state.historyIndex < state.history.length - 1) {
            const newIndex = state.historyIndex + 1;
            const futureState = state.history[newIndex];
            return {
                elements: futureState.elements,
                aiImage: futureState.aiImage,
                canvas: futureState.canvas,
                historyIndex: newIndex,
                selectedElementIds: []
            };
        }
        return state;
    }),

    goToHistory: (index) => set((state) => {
        if (index >= 0 && index < state.history.length) {
            const targetState = state.history[index];
            return {
                elements: targetState.elements,
                aiImage: targetState.aiImage,
                canvas: targetState.canvas,
                historyIndex: index,
                selectedElementIds: []
            };
        }
        return state;
    }),

    // --- Actions Globales ---
    clearProject: () => set({ 
        elements: [], 
        selectedElementIds: [], 
        aiImage: { url: null, x: 100, y: 50, width: 200, height: 150 }, 
        history: [], 
        historyIndex: -1,
        canvas: { ...get().canvas, panX: 0, panY: 0 }
    }),

    // Un changement de FORMAT retrace les découpes générées (utils/decoupe) : la
    // découpe suit le format fini. Les autres réglages du canvas (zoom, défilement,
    // repères…) ne touchent pas aux calques.
    setCanvas: (partial) => set((state) => {
        const canvas = { ...state.canvas, ...partial };
        const format = ('width_mm' in partial || 'height_mm' in partial)
            && (canvas.width_mm !== state.canvas.width_mm || canvas.height_mm !== state.canvas.height_mm);
        if (!format || !state.elements.some((e) => e.decoupe?.auto)) return { canvas };
        try {
            const r = regenererDecoupesAuto(state.elements, canvas, nouvelIdCalque);
            return r.regeneres ? { canvas, elements: r.elements } : { canvas };
        } catch {
            return { canvas };   // format en cours de saisie (vide, nul) : on attend le suivant
        }
    }),

    // ─── Tracé de découpe (CutContour) ───
    // « Ajouter » pose une découpe au premier plan, verrouillée, et la sélectionne.
    // S'il en existe déjà une, il la sélectionne SANS la remplacer : une découpe
    // importée ou de l'atelier ne se perd pas d'un clic. Renvoie { id, cree }.
    ajouterDecoupe: (options) => {
        const st = get();
        const existante = st.elements.find(estDecoupe);
        if (existante) {
            set({ selectedElementIds: [existante.id] });
            return { id: existante.id, cree: false };
        }
        const r = poserDecoupe(st.elements, st.canvas, options, nouvelIdCalque);
        set({ elements: r.elements, selectedElementIds: [r.calqueId] });
        get()._pushState('Tracé de découpe');
        return { id: r.calqueId, cree: true };
    },

    // Remonte une découpe au premier plan, SEULE (cf. utils/decoupe).
    placerDecoupeAuPremierPlan: (id) => {
        const st = get();
        set({ elements: decoupeAuPremierPlan(st.elements, id, nouvelIdCalque) });
        get()._pushState('Découpe au premier plan');
    },

    // Règle une découpe : la retrace au format avec ces réglages, en gardant son
    // identifiant, son nom, son groupe, son verrou et sa place dans la pile. Une
    // découpe importée devient une découpe générée, qui suivra le format.
    // Lève (code ARGUMENT_INVALIDE) sur un réglage invalide : l'inspecteur l'affiche.
    reglerDecoupe: (id, options) => {
        const st = get();
        const el = st.elements.find((e) => e.id === id);
        if (!el || !estDecoupe(el)) return;
        const reglage = reglagesDecoupe(st.canvas, options);   // un rayon au-delà du format est refusé
        const { calque } = genererDecoupe(st.canvas, reglage, nouvelIdCalque);
        const patch = {
            x: calque.x, y: calque.y, width: calque.width, height: calque.height, rotation: 0,
            svgPath: calque.svgPath, svgViewBox: calque.svgViewBox, svgFill: false,
            strokeWidth: calque.strokeWidth, color: calque.color, isCutContour: true,
            decoupe: { ...reglage, auto: true },
        };
        set({ elements: st.elements.map((e) => (e.id === id ? { ...e, ...patch } : e)) });
    },

    // L'encre SEULE : la couleur du trait et `decoupe.encre`, sans retracer. Passée par
    // `reglerDecoupe`, elle remplaçait un tracé importé (ovale, forme sur mesure) par
    // un rectangle au format, et rendait « générée » une découpe de l'atelier.
    reglerEncreDecoupe: (id, encre) => {
        if (!ENCRES_DECOUPE[encre]) throw new Error(`Encre de découpe inconnue : « ${encre} » (C, M, J ou N).`);
        const st = get();
        const el = st.elements.find((e) => e.id === id);
        if (!el || !estDecoupe(el)) return;
        const patch = { color: ENCRES_DECOUPE[encre].ecran, decoupe: { ...(el.decoupe || {}), encre } };
        set({ elements: st.elements.map((e) => (e.id === id ? { ...e, ...patch } : e)) });
    },

    setUiTheme: (theme) => set({ uiTheme: theme }),

    setAiImage: (partial) => set((state) => ({ aiImage: { ...state.aiImage, ...partial } })),

    selectElement: (id, multi = false) => set((state) => {
        if (id === null) {
            if (state.selectedElementIds.length === 0) return state;
            return { selectedElementIds: [] };
        }
        if (multi) {
            const isSelected = state.selectedElementIds.includes(id);
            return {
                selectedElementIds: isSelected
                    ? state.selectedElementIds.filter(e => e !== id)
                    : [...state.selectedElementIds, id]
            };
        }
        // OPTIMISATION VITALE : Empêche un re-render complet si l'élément est DÉJÀ sélectionné.
        // Cela empêche la destruction/recréation du noeud DOM (.drag-handle) juste sous le 
        // curseur lors du deuxième clic, ce qui cassait l'événement mouseup de react-rnd !
        if (!multi && state.selectedElementIds.length === 1 && state.selectedElementIds[0] === id) {
            return state;
        }
        return { selectedElementIds: [id] };
    }),

    updateElement: (id, partial) => set((state) => ({
        elements: state.elements.map(el => el.id === id ? { ...el, ...partial } : el)
    })),

    updateElements: (ids, partial) => set((state) => ({
        elements: state.elements.map(el => ids.includes(el.id) ? { ...el, ...partial } : el)
    })),

    // Applique une map { [id]: partial } en UNE seule mutation atomique (chaque
    // calque reçoit un patch différent). Utilisé par l'application d'un modèle
    // réglementaire (.lfgr) : placement + mise en forme par calque, en un seul
    // re-render. Renvoie le nombre de calques effectivement patchés.
    patchElements: (patchMap) => set((state) => ({
        elements: state.elements.map(el => patchMap[el.id] ? { ...el, ...patchMap[el.id] } : el)
    })),

    addElement: (element) => set((state) => {
        let defaultName = 'Calque ' + (state.elements.length + 1);
        let layerColor = 'transparent';
        const type = element.type || 'unknown';

        // Calques réglementaires : orange uniforme, nom déjà préfixé "REG ·"
        // par le builder. On bypass complètement la logique de naming/couleur
        // automatique pour garantir la cohérence visuelle dans la liste.
        if (element.regBlock) {
            const regAutoId = 'el_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5);
            const regFinalId = element.id || regAutoId;
            return {
                elements: [...state.elements, {
                    id: regFinalId,
                    locked: false,
                    hidden: false,
                    layerColor: '#F97316',  // orange-500 — tous les REG dans la liste
                    fillType: 'solid',
                    gradient: { type: 'linear', angle: 90, stops: [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }] },
                    strokeEnabled: false,
                    strokeType: 'solid',
                    strokeWidth: 2,
                    strokeColor: '#FFFFFF',
                    strokeGradient: { type: 'linear', angle: 90, stops: [{ color: '#FFFFFF', position: 0 }, { color: '#000000', position: 100 }] },
                    shadowEnabled: false,
                    shadowColor: '#000000',
                    shadowOpacity: 75,
                    shadowAngle: 120,
                    shadowDistance: 5,
                    shadowSpread: 0,
                    shadowSize: 5,
                    shadowInset: false,
                    ...element,
                    id: regFinalId,   // force la cohérence même si ...element l'écrase
                }],
                selectedElementIds: [regFinalId],
            };
        }

        if (type === 'image') {
            if (element.isAI) {
                defaultName = 'IA ' + (state.elements.filter(e => e.type === 'image' && e.isAI).length + 1);
                const blueColors = ['#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8'];
                layerColor = blueColors[Math.floor(Math.random() * blueColors.length)];
            } else {
                defaultName = 'Image ' + (state.elements.filter(e => e.type === 'image' && !e.isAI).length + 1);
                const greenColors = ['#34d399', '#10b981', '#059669', '#047857'];
                layerColor = greenColors[Math.floor(Math.random() * greenColors.length)];
            }
        } else if (type === 'text') {
            defaultName = 'Texte ' + (state.elements.filter(e => e.type === 'text').length + 1);
            const redColors = ['#f87171', '#ef4444', '#dc2626', '#b91c1c'];
            layerColor = redColors[Math.floor(Math.random() * redColors.length)];
        } else if (type === 'barcode') {
            defaultName = 'Code ' + (state.elements.filter(e => e.type === 'barcode').length + 1);
            const yellowColors = ['#fcd34d', '#facc15', '#eab308', '#ca8a04'];
            layerColor = yellowColors[Math.floor(Math.random() * yellowColors.length)];
        } else if (type === 'shape') {
            defaultName = 'Forme ' + (state.elements.filter(e => e.type === 'shape').length + 1);
            const purpleColors = ['#c084fc', '#a855f7', '#9333ea', '#7e22ce'];
            layerColor = purpleColors[Math.floor(Math.random() * purpleColors.length)];
        }

        const autoId = 'el_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5);
        // Si l'appelant fournit un id explicite (duplication / paste), on le respecte ;
        // sinon on en génère un nouveau. Dans les deux cas on auto-sélectionne le calque créé.
        const finalId = element.id || autoId;
        return {
            elements: [...state.elements, {
                id: autoId,
                name: element.name || defaultName,
                locked: false,
                hidden: false,
                layerColor: layerColor,
                fillType: 'solid',
                gradient: { type: 'linear', angle: 90, stops: [{ color: element.color || '#000000', position: 0 }, { color: '#ffffff', position: 100 }] },
                strokeEnabled: false,
                strokeType: 'solid',
                strokeWidth: 2,
                strokeColor: '#FFFFFF',
                strokeGradient: { type: 'linear', angle: 90, stops: [{ color: '#FFFFFF', position: 0 }, { color: '#000000', position: 100 }] },
                // Ombre portée (Photoshop-identical parameters)
                shadowEnabled: false,
                shadowColor: '#000000',
                shadowOpacity: 75,       // 0-100% (PS default: 75%)
                shadowAngle: 120,        // degrees, PS convention (PS default: 120°)
                shadowDistance: 5,        // px — distance (PS default: 5)
                shadowSpread: 0,         // 0-100% — portion solide de l'ombre (PS "Spread")
                shadowSize: 5,           // px — rayon total de l'ombre (PS "Size")
                shadowInset: false,
                ...element,
            }],
            // Auto-sélection du calque qui vient d'être créé
            selectedElementIds: [finalId],
        };
    }),

    removeElements: (ids) => {
        // Push current state to history BEFORE removing (so undo restores them)
        const before = useLabelStore.getState();
        // Cascade : supprimer un groupe supprime tout son sous-arbre.
        const full = new Set(ids);
        for (const id of ids) {
            const e = before.elements.find(x => x.id === id);
            if (e && e.type === 'group') descendantIds(before.elements, id).forEach(d => full.add(d));
        }
        const snapshot = {
            elements: JSON.parse(JSON.stringify(before.elements)),
            aiImage: JSON.parse(JSON.stringify(before.aiImage)),
            canvas: JSON.parse(JSON.stringify(before.canvas)),
            actionName: full.size > 1 ? `Suppression ${full.size} calques` : 'Suppression calque'
        };
        const newHistory = before.history.slice(0, before.historyIndex + 1);
        newHistory.push(snapshot);
        if (newHistory.length > 50) newHistory.shift();

        set((state) => ({
            elements: state.elements.filter(el => !full.has(el.id)),
            selectedElementIds: [],
            history: newHistory,
            historyIndex: newHistory.length - 1,
        }));
    },

    reorderElement: (id, direction) => set((state) => {
        const index = state.elements.findIndex(e => e.id === id);
        if (index < 0) return state;
        const newEls = [...state.elements];
        const el = newEls.splice(index, 1)[0];

        if (direction === 'up' && index < newEls.length) {
            newEls.splice(index + 1, 0, el);
        } else if (direction === 'down' && index > 0) {
            newEls.splice(index - 1, 0, el);
        } else if (direction === 'top') {
            newEls.push(el);
        } else if (direction === 'bottom') {
            newEls.unshift(el);
        } else {
            newEls.splice(index, 0, el);
        }
        return { elements: newEls };
    }),

    // Avant, lier une sélection écrivait un linkedId neuf sans regarder les liens
    // existants : un calque déjà lié à d'autres en était ARRACHÉ en silence, et le
    // lien d'origine pouvait se retrouver avec un seul membre — un lien qui ne lie
    // plus rien, mais qui affiche encore son icône. On absorbe donc les liens touchés
    // en entier : les compagnons du calque sélectionné le suivent dans le nouveau lien.
    // Lier la sélection. Un calque déjà lié ailleurs QUITTE son lien d'origine :
    // c'est le geste attendu, celui d'Illustrator, et le seul qui permette de sortir
    // un calque d'un lien pour le rattacher à un autre.
    //
    // Ce qui manquait, et qui causait la corruption : le lien abandonné n'était pas
    // nettoyé. Un lien réduit à un seul membre survivait, invisible et sans effet —
    // le calque restant se croyait solidaire de personne. `unlinkElement` faisait
    // pourtant ce nettoyage depuis toujours ; les deux chemins sont désormais
    // d'accord.
    linkElements: (ids) => set((state) => {
        const sel = new Set(ids);
        // Un lien d'un seul membre ne veut rien dire, et c'est exactement ce que
        // `unlinkElement` dissout à l'autre bout.
        if (sel.size < 2) return state;

        const quittes = new Set(
            state.elements.filter(el => sel.has(el.id) && el.linkedId).map(el => el.linkedId)
        );
        const groupId = 'group_' + Date.now();
        let elements = state.elements.map(el => sel.has(el.id) ? { ...el, linkedId: groupId } : el);

        for (const lien of quittes) {
            if (lien === groupId) continue;
            const restants = elements.filter(el => el.linkedId === lien);
            if (restants.length === 1) {
                elements = elements.map(el => el.id === restants[0].id ? { ...el, linkedId: null } : el);
            }
        }
        return { elements };
    }),

    unlinkElement: (id) => set((state) => {
        const targetEl = state.elements.find(e => e.id === id);
        if (!targetEl || !targetEl.linkedId) return state;

        const linkIdToCheck = targetEl.linkedId;

        let newElements = state.elements.map(el => el.id === id ? { ...el, linkedId: null } : el);

        const remainingWithLink = newElements.filter(el => el.linkedId === linkIdToCheck);
        if (remainingWithLink.length === 1) {
            newElements = newElements.map(el => el.id === remainingWithLink[0].id ? { ...el, linkedId: null } : el);
        }

        return { elements: newElements };
    }),

    duplicateElements: (ids) => set((state) => {
        // Dupliquer un groupe duplique tout son sous-arbre (en remappant les parentId).
        const sel = new Set(ids);
        for (const id of ids) {
            const e = state.elements.find(x => x.id === id);
            if (e && e.type === 'group') descendantIds(state.elements, id).forEach(d => sel.add(d));
        }
        const toDuplicate = state.elements.filter(el => sel.has(el.id));
        if (toDuplicate.length === 0) return state;

        // Map ancien id -> nouvel id (pour recâbler la hiérarchie dupliquée)
        const idMap = new Map();
        let seq = 0;
        for (const el of toDuplicate) idMap.set(el.id, 'el_' + Date.now() + '_' + (seq++) + Math.random().toString(36).substr(2, 4));

        const duplicated = toDuplicate.map(el => {
            const cloned = {
                ...el,
                id: idMap.get(el.id),
                // parent dupliqué → pointe vers le clone ; parent non dupliqué → on
                // GARDE le parent d'origine. Avant, ce cas retombait sur `null` : dupliquer
                // un sous-groupe imbriqué, ou un seul calque pris dans un groupe, éjectait
                // la copie à la racine du document, loin de l'original, où l'utilisateur
                // la croyait perdue.
                parentId: el.parentId ? (idMap.has(el.parentId) ? idMap.get(el.parentId) : el.parentId) : null,
                name: (el.name || el.type) + ' (copie)',
                linkedId: null,
            };
            // décalage +5 mm sur tout le sous-arbre (modèle logique : coords absolues)
            if (typeof cloned.x === 'number') cloned.x = cloned.x + 5;
            if (typeof cloned.y === 'number') cloned.y = cloned.y + 5;
            return cloned;
        });
        // re-sélectionne les clones des éléments d'origine sélectionnés
        const newSel = ids.map(id => idMap.get(id)).filter(Boolean);
        return {
            elements: [...state.elements, ...duplicated],
            selectedElementIds: newSel.length ? newSel : duplicated.map(d => d.id)
        };
    }),

    // ─────────────────────────────────────────────
    // GROUPES (hiérarchie de calques type Illustrator)
    // Modèle additif : type:'group' + parentId, tableau plat (cf. utils/layerTree).
    // ─────────────────────────────────────────────

    // Crée un groupe à partir de la sélection courante. Renvoie l'id du groupe.
    createGroupFromSelection: (name) => {
        const before = useLabelStore.getState();
        const ids = (before.selectedElementIds || []).slice();
        if (ids.length < 1) return null;
        const gid = 'grp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5);
        const nGroups = before.elements.filter(e => e.type === 'group').length;
        const groupNode = {
            id: gid, name: name || ('Groupe ' + (nGroups + 1)),
            layerColor: '#94a3b8', locked: false, hidden: false,
        };
        const newEls = groupSelection(before.elements, ids, groupNode);
        if (newEls === before.elements) return null; // rien à grouper
        const snapshot = {
            elements: JSON.parse(JSON.stringify(before.elements)),
            aiImage: JSON.parse(JSON.stringify(before.aiImage)),
            canvas: JSON.parse(JSON.stringify(before.canvas)),
            actionName: 'Grouper'
        };
        const newHistory = before.history.slice(0, before.historyIndex + 1);
        newHistory.push(snapshot);
        if (newHistory.length > 50) newHistory.shift();
        set({ elements: newEls, selectedElementIds: [gid], history: newHistory, historyIndex: newHistory.length - 1 });
        return gid;
    },

    // Dégroupe les groupes sélectionnés (enfants remontés au parent). Sélectionne les enfants libérés.
    ungroupSelection: () => {
        const before = useLabelStore.getState();
        const ids = before.selectedElementIds || [];
        const groups = before.elements.filter(e => ids.includes(e.id) && e.type === 'group');
        if (groups.length === 0) return;
        let els = before.elements;
        const freed = [];
        for (const g of groups) {
            childrenOf(els, g.id).forEach(c => freed.push(c.id));
            els = ungroupTree(els, g.id);
        }
        const snapshot = {
            elements: JSON.parse(JSON.stringify(before.elements)),
            aiImage: JSON.parse(JSON.stringify(before.aiImage)),
            canvas: JSON.parse(JSON.stringify(before.canvas)),
            actionName: 'Dégrouper'
        };
        const newHistory = before.history.slice(0, before.historyIndex + 1);
        newHistory.push(snapshot);
        if (newHistory.length > 50) newHistory.shift();
        set({ elements: els, selectedElementIds: freed, history: newHistory, historyIndex: newHistory.length - 1 });
    },

    // Étend une liste d'ids pour inclure les descendants des groupes (cascade de déplacement).
    expandWithDescendants: (ids) => {
        const els = useLabelStore.getState().elements;
        const out = new Set(ids);
        for (const id of ids) {
            const e = els.find(x => x.id === id);
            if (e && e.type === 'group') descendantIds(els, id).forEach(d => out.add(d));
        }
        return Array.from(out);
    },

    // Bascule replier/déplier d'un groupe dans le panneau Calques.
    // Le panneau considère un groupe DÉPLIÉ dès que isExpanded !== false, donc un
    // groupe dépourvu du champ (arbre importé, projet ancien) s'affiche déplié.
    // La bascule faisait `!el.isExpanded`, ce qui renvoyait `true` sur un champ
    // absent : le premier clic « dépliait » un groupe déjà déplié et ne changeait
    // rien à l'écran, il fallait cliquer DEUX fois pour le replier. On repart donc
    // de l'état tel que le panneau le lit.
    toggleGroupExpanded: (id) => set((state) => ({
        elements: state.elements.map(el => el.id === id && el.type === 'group' ? { ...el, isExpanded: el.isExpanded === false } : el)
    })),

    // Ajoute un ARBRE d'éléments importés (avec parentId) en préservant la
    // hiérarchie : ids provisoires remappés en ids uniques, parentId recâblés.
    // Utilisé par l'import PDF « éclaté vectoriel ».
    addElementsTree: (incoming) => set((state) => {
        if (!incoming || !incoming.length) return state;
        return {
            elements: [...state.elements, ...remapTreeIds(incoming)],
            selectedElementIds: [],
        };
    }),

    // Déplace un calque (drag-and-drop dans le panneau) : où = 'above'|'below'|'inside'.
    // Réordonne + reparente (avec garde anti-cycle), pousse l'historique.
    moveElement: (dragId, targetId, where) => {
        const before = useLabelStore.getState();
        const newEls = moveNode(before.elements, dragId, targetId, where);
        if (newEls === before.elements) return;
        const snapshot = {
            elements: JSON.parse(JSON.stringify(before.elements)),
            aiImage: JSON.parse(JSON.stringify(before.aiImage)),
            canvas: JSON.parse(JSON.stringify(before.canvas)),
            actionName: 'Réorganiser calques'
        };
        const newHistory = before.history.slice(0, before.historyIndex + 1);
        newHistory.push(snapshot);
        if (newHistory.length > 50) newHistory.shift();
        set({ elements: newEls, history: newHistory, historyIndex: newHistory.length - 1 });
    },

    loadProject: (projectData) => set({
        canvas: projectData.canvas,
        elements: projectData.elements,
        aiImage: projectData.aiImage,
        selectedElementIds: [],
        // Réinitialise l'historique sur l'état CHARGÉ. Sans ça, l'historique
        // d'avant le chargement (souvent l'état VIDE du démarrage) persistait,
        // et un undo après ouverture ramenait à un document BLANC.
        history: [{
            elements: JSON.parse(JSON.stringify(projectData.elements || [])),
            aiImage: JSON.parse(JSON.stringify(projectData.aiImage || null)),
            canvas: JSON.parse(JSON.stringify(projectData.canvas || {})),
            actionName: 'Document chargé',
        }],
        historyIndex: 0,
    }),

    // --- Alignement / Distribution ---

    /**
     * Aligne les calques sélectionnés. Tout le calcul vit dans `utils/alignement.js`,
     * fonction pure éprouvée par `banc/banc-alignement.mjs` ; le magasin ne fait
     * qu'appliquer son résultat.
     *
     * À plusieurs, les calques s'alignent entre eux. À UN SEUL, sur le cadre de
     * l'étiquette — ce qui était refusé jusque-là, donc centrer un objet seul
     * n'existait pas.
     */
    alignElements: (ids, direction) => set((state) => {
        const elements = calculerAlignement(state.elements, ids, direction, state.canvas);
        return elements ? { elements } : state;
    }),

    distributeElements: (ids, axis) => set((state) => {
        const targets = state.elements.filter(el => ids.includes(el.id) && !el.locked && el.type !== 'guide');
        if (targets.length < 3) return state;

        const sorted = [...targets].sort((a, b) => axis === 'horizontal' ? a.x - b.x : a.y - b.y);
        const first = sorted[0];
        const last = sorted[sorted.length - 1];

        if (axis === 'horizontal') {
            const totalSpace = (last.x + last.width) - first.x;
            const totalWidths = sorted.reduce((sum, el) => sum + el.width, 0);
            const gap = (totalSpace - totalWidths) / (sorted.length - 1);
            let currentX = first.x;
            const updates = {};
            sorted.forEach((el, i) => {
                updates[el.id] = currentX;
                currentX += el.width + gap;
            });
            return {
                elements: state.elements.map(el => updates[el.id] !== undefined ? { ...el, x: Math.round(updates[el.id] * 10) / 10 } : el)
            };
        } else {
            const totalSpace = (last.y + last.height) - first.y;
            const totalHeights = sorted.reduce((sum, el) => sum + el.height, 0);
            const gap = (totalSpace - totalHeights) / (sorted.length - 1);
            let currentY = first.y;
            const updates = {};
            sorted.forEach((el, i) => {
                updates[el.id] = currentY;
                currentY += el.height + gap;
            });
            return {
                elements: state.elements.map(el => updates[el.id] !== undefined ? { ...el, y: Math.round(updates[el.id] * 10) / 10 } : el)
            };
        }
    }),

    // Génération rapide d'une config JSON pour la sauvegarde
    getProjectData: () => {
        const state = get();
        return {
            canvas: state.canvas,
            elements: state.elements,
            aiImage: state.aiImage
        };
    }
}), {
    name: 'labelforge-storage',
    // Use IndexedDB instead of localStorage:
    //   - No 5MB quota (base64 images preserved across reloads)
    //   - Async writes → no main-thread blocking during drag/resize
    storage: createJSONStorage(() => idbStorage),
    // Only history is excluded — it grows large and rebuilds from scratch on reload
    partialize: (state) => ({
        canvas: state.canvas,
        elements: state.elements,
        aiImage: state.aiImage,
        aiModel: state.aiModel,
        uiTheme: state.uiTheme,
        textEditVeil: state.textEditVeil,
        autoFitOnImport: state.autoFitOnImport,
        // Préférences d'outillage (pas `activeTool` : cf. commentaire à sa déclaration).
        // `selection` est le RÉGLAGE (forme armée, adoucissement) ; la zone tracée
        // elle-même (`marquee`) reste volontairement hors persistance.
        eraser: state.eraser,
        selection: state.selection,
        toolPalette: state.toolPalette,
    }),
    // Migration des anciens utilisateurs au rehydrate :
    // - uiTheme 'forge-light' (theme generique gris retire) → 'forge-dark-light'
    onRehydrateStorage: () => (state) => {
        if (!state) return;
        if (state.uiTheme === 'forge-light') {
            state.uiTheme = 'forge-dark-light';
        }
    },
}));
